import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  SimpleGrid,
  Stack,
  VStack,
  HStack,
  Flex,
  Wrap,
  WrapItem,
  Text,
  Link,
  Image,
  Icon,
  IconButton,
  Divider,
} from "@chakra-ui/react";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { MapPin, Phone, Mail, Clock, ExternalLink, ArrowRight } from "lucide-react";
import NTPLogo from "ntp-logo-react";
import { useLocalPath, useText } from "../../i18n/lang";

const DOCS = "https://www.travel-time.ro/images/documente/";
const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=Strada+Popa+Tatu+49%2C+Sector+1%2C+Bucure%C8%99ti";

const TEXT = {
  en: {
    description:
      "Corporate travel management, MICE events and B2B flight consolidation from Bucharest, since 2005.",
    company: "Company",
    links: {
      about: "About us",
      services: "Services",
      consultancy: "Consultancy",
      press: "In the press",
      faq: "FAQ",
      contact: "Contact",
    },
    agencies: "For travel agencies",
    agencyLinks: {
      bookingTime: "Booking Time platform",
      demo: "Request a demo account",
      login: "Partner login",
      holidays: "Holidays",
    },
    contact: "Contact",
    address: "Popa Tatu Street 49, Sector 1, Bucharest",
    emails: [
      { label: "Partner agencies", email: "parteneri@travel-time.ro" },
      { label: "Ticketing", email: "ticketing@travel-time.ro" },
      { label: "Leisure travel", email: "turism@travel-time.ro" },
    ],
    hours: "Mon - Fri, 09:00 - 18:00",
    emergency: "24/7 emergency support for corporate clients and partner agencies",
    allContacts: "All contact details",
    documents: "Documents and certificates",
    docs: {
      license: "Tourism license no. 236 / 28.04.2026",
      iata: "IATA certificate",
      registration: "Registration certificate",
      tourismCert: "Tourism certificate (brevet)",
      insurance: "Insolvency insurance policy",
      iso9001: "ISO 9001:2015 certificate",
      iso14001: "ISO 14001:2015 certificate",
    },
    legal: "Legal information",
    legalLinks: {
      terms: "Terms and conditions",
      contract: "Tourist contract",
      gdpr: "Privacy policy (GDPR)",
      cookies: "Cookies policy",
      cookieSettings: "Cookie settings",
      anpcMeasures: "Mandatory ANPC measures",
      euProjects: "EU-funded projects",
    },
    rights: "All rights reserved.",
  },
  ro: {
    description:
      "Managementul călătoriilor corporate, evenimente MICE și consolidare B2B de bilete de avion, din București, din 2005.",
    company: "Companie",
    links: {
      about: "Despre noi",
      services: "Servicii",
      consultancy: "Consultanță",
      press: "În presă",
      faq: "Întrebări frecvente",
      contact: "Contact",
    },
    agencies: "Pentru agenții de turism",
    agencyLinks: {
      bookingTime: "Platforma Booking Time",
      demo: "Solicită un cont demo",
      login: "Autentificare parteneri",
      holidays: "Vacanțe",
    },
    contact: "Contact",
    address: "Str. Popa Tatu nr. 49, Sector 1, București",
    emails: [
      { label: "Agenții partenere", email: "parteneri@travel-time.ro" },
      { label: "Ticketing", email: "ticketing@travel-time.ro" },
      { label: "Turism", email: "turism@travel-time.ro" },
    ],
    hours: "Luni - vineri, 09:00 - 18:00",
    emergency: "Asistență 24/7 pentru urgențe, pentru clienții corporate și agențiile partenere",
    allContacts: "Toate datele de contact",
    documents: "Documente și certificări",
    docs: {
      license: "Licență de turism nr. 236 / 28.04.2026",
      iata: "Certificat IATA",
      registration: "Certificat de înregistrare",
      tourismCert: "Brevet de turism",
      insurance: "Poliță de asigurare insolvabilitate",
      iso9001: "Certificat ISO 9001:2015",
      iso14001: "Certificat ISO 14001:2015",
    },
    legal: "Informații legale",
    legalLinks: {
      terms: "Termeni și condiții",
      contract: "Contract cu turistul",
      gdpr: "Politica de confidențialitate (GDPR)",
      cookies: "Politica de cookies",
      cookieSettings: "Setări cookie",
      anpcMeasures: "Măsuri obligatorii ANPC",
      euProjects: "Proiecte cu finanțare europeană",
    },
    rights: "Toate drepturile rezervate.",
  },
};

const DOCUMENTS = [
  ["license", "Licenta turism TRAVEL TIME D&R - 2026.pdf"],
  ["iata", "Certificat IATA 2026.pdf"],
  ["registration", "CUI-martie 2026 - TRAVEL TIME.pdf"],
  ["tourismCert", "BREVET Turism.pdf"],
  ["insurance", "Polita insolvabilitate TRAVEL TIME D&R.pdf"],
  ["iso9001", "ISO 9001 TRAVEL TIME DR SRL.pdf"],
  ["iso14001", "ISO14001 TRAVEL TIME DR SRL.pdf"],
];

const SOCIAL = [
  { label: "LinkedIn", icon: FaLinkedinIn, href: "https://www.linkedin.com/company/travel-time-travel-agency/" },
  { label: "Facebook", icon: FaFacebookF, href: "https://www.facebook.com/traveltimeromania" },
  { label: "Instagram", icon: FaInstagram, href: "https://www.instagram.com/vacantetraveltime/" },
];

const linkStyle = {
  color: "blue.100",
  fontSize: "md",
  _hover: { color: "white", textDecoration: "none" },
};

const ColumnTitle = ({ children }) => (
  <Text
    as="h2"
    fontFamily="Montserrat"
    fontWeight="700"
    fontSize="sm"
    letterSpacing="wider"
    textTransform="uppercase"
    color="white"
    mb={4}
  >
    {children}
  </Text>
);

// Link intern: navigare în aplicație + derulare la începutul paginii
const InternalLink = ({ to, children, keepScroll }) => (
  <Link
    as={RouterLink}
    to={to}
    onClick={keepScroll ? undefined : () => window.scrollTo(0, 0)}
    {...linkStyle}
  >
    {children}
  </Link>
);

const ExternalLinkItem = ({ href, children }) => (
  <Link href={href} isExternal {...linkStyle} display="inline-flex" alignItems="center" gap={1.5}>
    {children}
    <Icon as={ExternalLink} boxSize={3.5} opacity={0.7} />
  </Link>
);

const SmallLink = ({ href, children }) => (
  <Link
    href={href}
    isExternal
    color="blue.100"
    fontSize="sm"
    _hover={{ color: "white", textDecoration: "underline" }}
  >
    {children}
  </Link>
);

const Footer = ({ onOpenCookieSettings }) => {
  const t = useText(TEXT);
  const localPath = useLocalPath();

  return (
    <Box
      as="footer"
      bgGradient="linear(160deg, #0b1f3a 0%, #102f57 100%)"
      color="blue.100"
      borderTopWidth="1px"
      borderColor="whiteAlpha.200"
    >
      <Container maxW="container.xl" pt={{ base: 12, md: 16 }} pb={10}>
        {/* Rândul principal */}
        <SimpleGrid
          templateColumns={{ base: "1fr 1fr", lg: "1.5fr 1fr 1fr 1.5fr" }}
          spacingX={{ base: 6, lg: 10 }}
          spacingY={10}
        >
          <VStack align="flex-start" spacing={5} gridColumn={{ base: "span 2", lg: "auto" }}>
            <RouterLink to={localPath("/")} onClick={() => window.scrollTo(0, 0)} aria-label="Travel Time">
              <Image src="/images/traveltime-logo-white.png" alt="Travel Time logo" w="190px" h="auto" />
            </RouterLink>
            <Text fontSize="md" lineHeight="1.7" maxW="sm">
              {t.description}
            </Text>
            <HStack spacing={3}>
              {SOCIAL.map((s) => (
                <IconButton
                  key={s.label}
                  as="a"
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  icon={<Icon as={s.icon} boxSize={4} />}
                  isRound
                  variant="outline"
                  color="white"
                  borderColor="whiteAlpha.400"
                  _hover={{ bg: "whiteAlpha.200", borderColor: "white" }}
                />
              ))}
            </HStack>
          </VStack>

          <Box>
            <ColumnTitle>{t.company}</ColumnTitle>
            <Stack spacing={3}>
              <InternalLink to={localPath("/")}>{t.links.about}</InternalLink>
              <InternalLink to={localPath("/servicii")}>{t.links.services}</InternalLink>
              <InternalLink to={localPath("/capabilitati")}>{t.links.consultancy}</InternalLink>
              <InternalLink to={localPath("/presa")}>{t.links.press}</InternalLink>
              <InternalLink to={localPath("/faq")}>{t.links.faq}</InternalLink>
              <InternalLink to={localPath("/contact")}>{t.links.contact}</InternalLink>
            </Stack>
          </Box>

          <Box>
            <ColumnTitle>{t.agencies}</ColumnTitle>
            <Stack spacing={3}>
              <InternalLink to={localPath("/agentii-partenere")}>{t.agencyLinks.bookingTime}</InternalLink>
              <InternalLink to={`${localPath("/agentii-partenere")}#cont-demo`} keepScroll>
                {t.agencyLinks.demo}
              </InternalLink>
              <ExternalLinkItem href="https://tbs.flytime.ro/reseller/auth/">{t.agencyLinks.login}</ExternalLinkItem>
              <ExternalLinkItem href="https://vacante.travel-time.ro/?utm_source=travel-time.ro&utm_medium=footer">
                {t.agencyLinks.holidays}
              </ExternalLinkItem>
            </Stack>
          </Box>

          <Box gridColumn={{ base: "span 2", lg: "auto" }}>
            <ColumnTitle>{t.contact}</ColumnTitle>
            <Stack spacing={3.5} fontSize="md">
              <HStack align="flex-start" spacing={3}>
                <Icon as={MapPin} boxSize={5} color="orange.300" mt={0.5} flexShrink={0} />
                <Link href={MAPS_URL} isExternal {...linkStyle}>
                  {t.address}
                </Link>
              </HStack>
              <HStack spacing={3}>
                <Icon as={Phone} boxSize={5} color="orange.300" flexShrink={0} />
                <Link href="tel:+40371121272" {...linkStyle} color="white" fontWeight="600">
                  +40 371 12 12 72
                </Link>
              </HStack>
              <HStack align="flex-start" spacing={3}>
                <Icon as={Mail} boxSize={5} color="orange.300" mt={0.5} flexShrink={0} />
                <Stack spacing={1.5}>
                  {t.emails.map((e) => (
                    <Text key={e.email} lineHeight="1.4">
                      <Text as="span" color="blue.200" fontSize="sm">
                        {e.label}:{" "}
                      </Text>
                      <Link href={`mailto:${e.email}`} {...linkStyle}>
                        {e.email}
                      </Link>
                    </Text>
                  ))}
                </Stack>
              </HStack>
              <HStack align="flex-start" spacing={3}>
                <Icon as={Clock} boxSize={5} color="orange.300" mt={0.5} flexShrink={0} />
                <Box lineHeight="1.5">
                  <Text>{t.hours}</Text>
                  <Text fontSize="sm" color="blue.200">
                    {t.emergency}
                  </Text>
                </Box>
              </HStack>
              <Link
                as={RouterLink}
                to={localPath("/contact")}
                onClick={() => window.scrollTo(0, 0)}
                display="inline-flex"
                alignItems="center"
                gap={1.5}
                color="orange.300"
                fontWeight="600"
                _hover={{ color: "orange.200", textDecoration: "none" }}
              >
                {t.allContacts}
                <Icon as={ArrowRight} boxSize={4} />
              </Link>
            </Stack>
          </Box>
        </SimpleGrid>

        <Divider borderColor="whiteAlpha.200" my={{ base: 10, md: 12 }} />

        {/* Documente și informații legale */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 8, md: 12 }}>
          <Box>
            <ColumnTitle>{t.documents}</ColumnTitle>
            <SimpleGrid columns={2} spacingX={{ base: 4, md: 6 }} spacingY={2.5}>
              {DOCUMENTS.map(([key, file]) => (
                <SmallLink key={key} href={DOCS + encodeURIComponent(file)}>
                  {t.docs[key]}
                </SmallLink>
              ))}
            </SimpleGrid>
          </Box>
          <Box>
            <ColumnTitle>{t.legal}</ColumnTitle>
            <SimpleGrid columns={2} spacingX={{ base: 4, md: 6 }} spacingY={2.5}>
              <SmallLink href={DOCS + encodeURIComponent("Termeni si conditii TT.pdf")}>{t.legalLinks.terms}</SmallLink>
              <SmallLink href={DOCS + encodeURIComponent("Draft Contract Turist.pdf")}>{t.legalLinks.contract}</SmallLink>
              <SmallLink href={DOCS + encodeURIComponent("Politica GDPR TT.pdf")}>{t.legalLinks.gdpr}</SmallLink>
              <SmallLink href={DOCS + encodeURIComponent("Politica Cookies TT.pdf")}>{t.legalLinks.cookies}</SmallLink>
              <Link
                as="button"
                type="button"
                onClick={onOpenCookieSettings}
                textAlign="left"
                color="blue.100"
                fontSize="sm"
                _hover={{ color: "white", textDecoration: "underline" }}
              >
                {t.legalLinks.cookieSettings}
              </Link>
              <SmallLink href="https://legislatie.just.ro/Public/DetaliiDocument/257649">
                {t.legalLinks.anpcMeasures}
              </SmallLink>
              <SmallLink href="https://anpc.ro/">ANPC</SmallLink>
              <Link
                as={RouterLink}
                to="/proiecte-europene"
                onClick={() => window.scrollTo(0, 0)}
                color="blue.100"
                fontSize="sm"
                _hover={{ color: "white", textDecoration: "underline" }}
              >
                {t.legalLinks.euProjects}
              </Link>
            </SimpleGrid>
          </Box>
        </SimpleGrid>

        {/* Acreditări, plăți și soluționarea litigiilor */}
        <Flex
          mt={{ base: 10, md: 12 }}
          bg="white"
          borderRadius="xl"
          px={{ base: 4, md: 8 }}
          py={5}
          align="center"
          justify="center"
        >
          <Wrap spacing={{ base: 5, md: 8 }} align="center" justify="center">
            <WrapItem>
              <Image src="/images/iata.png" alt="IATA" h="34px" w="auto" />
            </WrapItem>
            <WrapItem>
              <Image src="/images/anat.png" alt="ANAT" h="34px" w="auto" />
            </WrapItem>
            <WrapItem>
              <Image src="/images/logo-travelnation.png" alt="Travel Nation" h="34px" w="auto" />
            </WrapItem>
            <WrapItem>
              <Box w={{ base: "200px", md: "220px" }}>
                <NTPLogo color="#74deec" version="orizontal" secret="136590" />
              </Box>
            </WrapItem>
            <WrapItem>
              <Link href="https://consumer-redress.ec.europa.eu/index_en" isExternal _hover={{ opacity: 0.8 }}>
                <Image src="/images/SOL.png" alt="Soluționarea online a litigiilor (SOL)" h="42px" w="auto" />
              </Link>
            </WrapItem>
            <WrapItem>
              <Link href="https://reclamatiisal.anpc.ro/" isExternal _hover={{ opacity: 0.8 }}>
                <Image src="/images/SAL.png" alt="ANPC - Soluționarea alternativă a litigiilor (SAL)" h="42px" w="auto" />
              </Link>
            </WrapItem>
          </Wrap>
        </Flex>
      </Container>

      {/* Bara de jos */}
      <Box borderTopWidth="1px" borderColor="whiteAlpha.200" py={5}>
        <Container maxW="container.xl">
          <Stack
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align="center"
            spacing={2}
            fontSize="sm"
            color="blue.200"
            textAlign="center"
          >
            <Text>
              © {new Date().getFullYear()} Travel Time D&R. {t.rights}
            </Text>
            <Text>SC TRAVEL TIME D&R SRL · CIF RO17926970 · Nr. Reg. Com. J2005015226401</Text>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
